import { useEffect } from 'react';
import { buildSiteUrl } from './localizedRoutes';
import { getCspNonce } from './csp';
import { LANGUAGE_CODES, LANGUAGES } from '../i18n/languages';

const baseUrl = 'https://www.miamitoursandtravel.com';
const defaultTitle = 'Miami Tours and Travel';
const defaultDescription = 'Book tours, transportation, rentals, and day trips from Miami in one place.';
const defaultCanonical = `${baseUrl}/`;

function setMetaTag(selector, attribute, value) {
  const element = document.querySelector(selector);
  if (element) {
    element.setAttribute(attribute, value);
  }
}

function ensureCanonicalTag() {
  let canonicalElement = document.querySelector('link[rel="canonical"]');

  if (!canonicalElement) {
    canonicalElement = document.createElement('link');
    canonicalElement.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalElement);
  }

  return canonicalElement;
}

function ensureAlternateTag(hreflang) {
  let alternateElement = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);

  if (!alternateElement) {
    alternateElement = document.createElement('link');
    alternateElement.setAttribute('rel', 'alternate');
    alternateElement.setAttribute('hreflang', hreflang);
    document.head.appendChild(alternateElement);
  }

  return alternateElement;
}

function getCanonicalPath(url) {
  if (!url) {
    return '/';
  }

  try {
    const parsedUrl = new URL(url, baseUrl);
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}` || '/';
  } catch {
    const normalizedUrl = String(url).startsWith(baseUrl)
      ? String(url).slice(baseUrl.length)
      : String(url);

    return normalizedUrl || '/';
  }
}

export function usePageMeta({ title, description, canonical, image, noIndex = false }) {
  useEffect(() => {
    const previousTitle = document.title;
    const descriptionElement = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionElement?.getAttribute('content') || defaultDescription;
    const canonicalElement = ensureCanonicalTag();
    const previousCanonical = canonicalElement.getAttribute('href') || defaultCanonical;
    const canonicalUrl = canonical || defaultCanonical;
    const canonicalPath = getCanonicalPath(canonicalUrl);
    const alternateElements = LANGUAGE_CODES.map((language) => ({
      language,
      element: ensureAlternateTag(LANGUAGES[language].hreflang),
    }));
    const alternateDefaultElement = ensureAlternateTag('x-default');
    const previousAlternates = alternateElements.map(({ language, element }) => ({
      language,
      element,
      href: element.getAttribute('href') || buildSiteUrl(canonicalPath, language),
    }));
    const previousAlternateDefault = alternateDefaultElement.getAttribute('href') || buildSiteUrl(canonicalPath, 'en');
    const robotsElement = document.querySelector('meta[name="robots"]');
    const previousRobots = robotsElement?.getAttribute('content') || '';
    const previousOgImage = document.querySelector('meta[property="og:image"]')?.getAttribute('content') || '';
    const previousTwitterImage = document.querySelector('meta[name="twitter:image"]')?.getAttribute('content') || '';

    document.title = title || defaultTitle;
    setMetaTag('meta[name="description"]', 'content', description || defaultDescription);
    setMetaTag('meta[property="og:title"]', 'content', title || defaultTitle);
    setMetaTag('meta[property="og:description"]', 'content', description || defaultDescription);
    setMetaTag('meta[name="twitter:title"]', 'content', title || defaultTitle);
    setMetaTag('meta[name="twitter:description"]', 'content', description || defaultDescription);
    if (image) {
      setMetaTag('meta[property="og:image"]', 'content', image);
      setMetaTag('meta[name="twitter:image"]', 'content', image);
    }
    setMetaTag('meta[property="og:url"]', 'content', canonicalUrl);
    canonicalElement.setAttribute('href', canonicalUrl);
    if (robotsElement) {
      robotsElement.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow');
    }
    alternateElements.forEach(({ language, element }) => element.setAttribute('href', buildSiteUrl(canonicalPath, language)));
    alternateDefaultElement.setAttribute('href', buildSiteUrl(canonicalPath, 'en'));

    return () => {
      document.title = previousTitle || defaultTitle;
      setMetaTag('meta[name="description"]', 'content', previousDescription);
      setMetaTag('meta[property="og:title"]', 'content', previousTitle || defaultTitle);
      setMetaTag('meta[property="og:description"]', 'content', previousDescription);
      setMetaTag('meta[name="twitter:title"]', 'content', previousTitle || defaultTitle);
      setMetaTag('meta[name="twitter:description"]', 'content', previousDescription);
      setMetaTag('meta[property="og:image"]', 'content', previousOgImage);
      setMetaTag('meta[name="twitter:image"]', 'content', previousTwitterImage);
      setMetaTag('meta[property="og:url"]', 'content', previousCanonical);
      canonicalElement.setAttribute('href', previousCanonical);
      if (robotsElement) {
        robotsElement.setAttribute('content', previousRobots || 'index, follow');
      }
      previousAlternates.forEach(({ element, href }) => element.setAttribute('href', href));
      alternateDefaultElement.setAttribute('href', previousAlternateDefault);
    };
  }, [title, description, canonical, image, noIndex]);
}

export function useStructuredData(data, id = 'structured-data') {
  useEffect(() => {
    if (!data) {
      return undefined;
    }

    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      const nonce = getCspNonce();
      if (nonce) {
        script.nonce = nonce;
      }
      document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(data);

    return () => {
      if (script?.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [data, id]);
}
